import { observer } from 'src/utils/mobx-react';
import React, { useMemo } from 'react';
import { DateTime } from 'src_common/utils/time/time';
import _ from 'lodash';
import { useAppStateContext } from 'src/AppState/AppState';
import DatePicker, { CalendarContainer, registerLocale } from 'react-datepicker';
import { CalendarWrapper, DatePickerWrapper } from './DateTimeInput.style';
import enGB from 'date-fns/locale/en-GB';

export type DateTimeInputPropsType = {
    className?: string;
    dataTest?: string;
    dateFormat?: string;
    maxDate?: string;
    minDate?: string;
    onChange?: (time: string | null) => void;
    rangeInput?: boolean;
    required?: boolean;
    timeFormat?: string;
    value: string;
};

export const DateTimeInput = observer('DateTimeInput', (props: DateTimeInputPropsType) => {
    const appState = useAppStateContext();

    const handleChange = (date: Date | null): void => {
        const { onChange, required = true } = props;

        if (date === null) {
            if (required) {
                appState.reduxAction.addError('Date format is wrong!');
            } else if (onChange !== undefined) {
                onChange(null);
            }
            return;
        }

        const parsedDate = DateTime.from(date);
        if (parsedDate === null || parsedDate.isValid() === false) {
            appState.reduxAction.addError('Date format is wrong!');
            return;
        }

        if (onChange !== undefined) {
            onChange(parsedDate.toISOString());
        }
    };

    const debouncedChangeHandler: _.DebouncedFunc<(date: Date | null) => void> = useMemo(
        () => _.debounce(handleChange, 500),
        []
    );

    const { dateFormat, timeFormat, value, dataTest, className } = props;
    const formatOfDate = dateFormat === undefined ? 'dd-MM-yyyy' : dateFormat;
    const formatOfTime = timeFormat === undefined ? 'HH:mm' : timeFormat;

    const valueFormat =
        dateFormat === undefined || timeFormat === undefined ? 'dd-MM-yyyy HH:mm' : `${formatOfDate} ${formatOfTime}`;
    const placeholder = valueFormat.toUpperCase();

    const currentValue = value === '' ? null : new Date(value);

    registerLocale('en-GB', enGB);

    const datePicker = (
        <DatePicker
            onChange={debouncedChangeHandler}
            selected={currentValue}
            showTimeSelect
            maxDate={props.maxDate === undefined ? null : new Date(props.maxDate)}
            minDate={props.minDate === undefined ? null : new Date(props.minDate)}
            placeholderText={placeholder}
            dateFormat={valueFormat}
            popperPlacement='bottom-start'
            locale='en-GB'
            //@ts-expect-error // error for 'className'
            calendarContainer={({ children, className }): JSX.Element => {
                return (
                    <CalendarWrapper>
                        <CalendarContainer className={className}>
                            <div
                                style={{ position: 'relative' }}
                                data-test='calendar-container'
                            >
                                {children}
                            </div>
                        </CalendarContainer>
                    </CalendarWrapper>
                );
            }}
        />
    );

    if (props.rangeInput === true) {
        return datePicker;
    }

    return (
        <DatePickerWrapper
            className={className}
            data-test={dataTest}
        >
            {datePicker}
        </DatePickerWrapper>
    );
});
